import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { format, subDays } from 'date-fns';
import {
  CalendarPlus, Users, Phone, Clock, User, Upload, CheckCircle2,
  XCircle, AlertTriangle, RotateCcw, RefreshCw, List, Crown, Hash,
  CalendarDays, ChevronLeft, ChevronRight, X,
} from 'lucide-react';

const STATUS_STYLES = {
  confirmed: { label: 'Confirmed', cls: 'bg-[#2D6A4F]/10 text-[#2D6A4F] border-[#2D6A4F]/20', icon: CheckCircle2 },
  arrived: { label: 'Arrived', cls: 'bg-[color:var(--color-primary)]/10 text-[color:var(--color-primary)] border-[color:var(--color-primary)]/20', icon: CheckCircle2 },
  cancelled: { label: 'Cancelled', cls: 'bg-[#C45A41]/10 text-[#C45A41] border-[#C45A41]/20', icon: XCircle },
  'no-show': { label: 'No-show', cls: 'bg-[#D4A017]/10 text-[#D4A017] border-[#D4A017]/20', icon: AlertTriangle },
  rescheduled: { label: 'Rescheduled', cls: 'bg-[#6366F1]/10 text-[#6366F1] border-[#6366F1]/20', icon: RefreshCw },
};

const EMPTY_FORM = {
  date: format(new Date(), 'yyyy-MM-dd'),
  time: '',
  guest_name: '',
  mobile: '',
  pax: '',
  reservation_type: 'table',
  event_type: '',
  notes: '',
  status: 'confirmed',
  send_confirmation: true,
  is_member: false,
  member_id: '',
};

export default function ManagerReservations() {
  const { getHeaders, API } = useAuth();
  const [tab, setTab] = useState('form');
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [imageFile, setImageFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listDate, setListDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [repeatGuest, setRepeatGuest] = useState(false);
  const [visitCount, setVisitCount] = useState(0);
  const [lookingUp, setLookingUp] = useState(false);
  const [dupWarning, setDupWarning] = useState(null);
  const [pendingSubmit, setPendingSubmit] = useState(false);
  const [listMode, setListMode] = useState('date'); // 'date' or 'upcoming'
  const [upcomingBookings, setUpcomingBookings] = useState([]);
  const [calendarData, setCalendarData] = useState(null);
  const [calMonth, setCalMonth] = useState(new Date().getMonth() + 1);
  const [calYear, setCalYear] = useState(new Date().getFullYear());
  const [calPopup, setCalPopup] = useState(null); // { date, guests }
  const lookupTimer = useRef(null);

  const loadReservations = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/reservations?date=${listDate}`, { headers: getHeaders() });
      setReservations(res.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [API, getHeaders, listDate]);

  useEffect(() => { loadReservations(); }, [loadReservations]);

  const loadUpcoming = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/reservations?mode=upcoming`, { headers: getHeaders() });
      setUpcomingBookings(res.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [API, getHeaders]);

  const loadCalendar = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/reservations/calendar?year=${calYear}&month=${calMonth}`, { headers: getHeaders() });
      setCalendarData(res.data);
    } catch { /* ignore */ }
  }, [API, getHeaders, calYear, calMonth]);

  useEffect(() => { if (tab === 'calendar') loadCalendar(); }, [tab, loadCalendar]);
  useEffect(() => { if (listMode === 'upcoming') loadUpcoming(); }, [listMode, loadUpcoming]);

  // Mobile auto-lookup with debounce
  const handleMobileChange = (val) => {
    const digits = val.replace(/\D/g, '').slice(0, 10);
    setForm(p => ({ ...p, mobile: digits }));
    setRepeatGuest(false);
    setVisitCount(0);

    if (lookupTimer.current) clearTimeout(lookupTimer.current);
    if (digits.length === 10) {
      setLookingUp(true);
      lookupTimer.current = setTimeout(async () => {
        try {
          const res = await axios.get(`${API}/reservations/lookup-guest?mobile=${digits}`, { headers: getHeaders() });
          if (res.data.found) {
            setForm(p => ({
              ...p,
              guest_name: res.data.guest_name || p.guest_name,
              is_member: res.data.is_member || false,
              member_id: res.data.member_id || '',
            }));
            setRepeatGuest(true);
            setVisitCount(res.data.visit_count || 0);
          }
        } catch { /* ignore */ }
        finally { setLookingUp(false); }
      }, 400);
    }
  };

  const doSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = { ...form, pax: parseInt(form.pax) || 1 };
      const res = await axios.post(`${API}/reservations`, payload, { headers: getHeaders() });
      const newId = res.data.id;

      if (imageFile && form.reservation_type === 'event') {
        const fd = new FormData();
        fd.append('file', imageFile);
        await axios.post(`${API}/reservations/${newId}/image`, fd, {
          headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' }
        });
      }

      toast.success('Reservation saved');
      setForm({ ...EMPTY_FORM, date: form.date });
      setImageFile(null);
      setRepeatGuest(false);
      setVisitCount(0);
      setListDate(form.date);
      loadReservations();
      setTab('list');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSubmitting(false); setPendingSubmit(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.mobile || form.mobile.length !== 10) {
      toast.error('Please enter a valid 10-digit mobile number');
      return;
    }
    if (!form.guest_name || !form.time || !form.pax) {
      toast.error('Please fill Guest Name, Time, and Pax');
      return;
    }
    if (form.is_member && !form.member_id.trim()) {
      toast.error('Member ID is required when Member is enabled');
      return;
    }

    // Duplicate check
    try {
      const dup = await axios.get(`${API}/reservations/check-duplicate?mobile=${form.mobile}&date=${form.date}`, { headers: getHeaders() });
      if (dup.data.duplicate) {
        setDupWarning(dup.data.existing);
        setPendingSubmit(true);
        return;
      }
    } catch { /* proceed */ }

    doSubmit();
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/reservations/${id}/status`, { status }, { headers: getHeaders() });
      toast.success(`Status updated to ${STATUS_STYLES[status]?.label || status}`);
      loadReservations();
    } catch { toast.error('Failed to update status'); }
  };

  const toggleMember = async (id, current) => {
    try {
      await axios.patch(`${API}/reservations/${id}/member`, { is_member: !current }, { headers: getHeaders() });
      toast.success(!current ? 'Marked as member' : 'Removed member tag');
      loadReservations();
    } catch { toast.error('Failed to update'); }
  };

  const isEvent = form.reservation_type === 'event';

  return (
    <div data-testid="manager-reservations-page">
      <div className="mb-5">
        <h2 className="text-xl sm:text-2xl tracking-tight font-semibold text-[#1A2421]">Reservations</h2>
        <p className="text-sm text-[#5C6B64] mt-1">Log table bookings & events</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[#F3F5F2] p-0.5 rounded-lg w-fit mb-5">
          <TabsTrigger value="form" className="data-[state=active]:bg-[color:var(--color-primary)] data-[state=active]:text-white text-sm px-4 gap-1.5"
            data-testid="res-tab-form">
            <CalendarPlus className="w-3.5 h-3.5" /> New Booking
          </TabsTrigger>
          <TabsTrigger value="list" className="data-[state=active]:bg-[color:var(--color-primary)] data-[state=active]:text-white text-sm px-4 gap-1.5"
            data-testid="res-tab-list">
            <List className="w-3.5 h-3.5" /> Bookings
          </TabsTrigger>
          <TabsTrigger value="calendar" className="data-[state=active]:bg-[color:var(--color-primary)] data-[state=active]:text-white text-sm px-4 gap-1.5"
            data-testid="res-tab-calendar">
            <CalendarDays className="w-3.5 h-3.5" /> Calendar
          </TabsTrigger>
        </TabsList>

        {/* ─── NEW BOOKING FORM ─── */}
        <TabsContent value="form">
          <Card className="border-[#E2E8DF] shadow-sm max-w-lg">
            <CardHeader className="pb-3">
              <CardTitle className="text-base text-[#1A2421] flex items-center gap-2">
                <CalendarPlus className="w-4 h-4 text-[color:var(--color-primary)]" strokeWidth={1.5} /> New Reservation
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-3">
                {/* Mobile first */}
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#5C6B64]">Mobile Number</Label>
                  <div className="relative">
                    <Phone className="absolute left-2.5 top-2.5 w-4 h-4 text-[#5C6B64]" />
                    <Input value={form.mobile}
                      onChange={e => handleMobileChange(e.target.value)}
                      placeholder="10-digit mobile number"
                      inputMode="numeric"
                      maxLength={10}
                      className="h-10 pl-9 border-[#E2E8DF]" data-testid="res-mobile" />
                    {lookingUp && (
                      <div className="absolute right-2.5 top-2.5">
                        <div className="animate-spin w-4 h-4 border-2 border-[color:var(--color-primary)] border-t-transparent rounded-full" />
                      </div>
                    )}
                  </div>
                  {form.mobile.length > 0 && form.mobile.length < 10 && (
                    <p className="text-[10px] text-[#C45A41]">{10 - form.mobile.length} more digit{10 - form.mobile.length !== 1 ? 's' : ''} needed</p>
                  )}
                  {repeatGuest && (
                    <div className="flex items-center gap-2">
                      <Badge className="text-[10px] bg-[color:var(--color-primary)]/10 text-[color:var(--color-primary)] border-[color:var(--color-primary)]/20">
                        <RefreshCw className="w-2.5 h-2.5 mr-1" />Repeat Guest · {visitCount} visit{visitCount !== 1 ? 's' : ''}
                      </Badge>
                    </div>
                  )}
                </div>

                {/* Guest name second */}
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#5C6B64]">Guest Name</Label>
                  <div className="relative">
                    <User className="absolute left-2.5 top-2.5 w-4 h-4 text-[#5C6B64]" />
                    <Input value={form.guest_name}
                      onChange={e => setForm(p => ({ ...p, guest_name: e.target.value }))}
                      placeholder="Guest name"
                      className="h-10 pl-9 border-[#E2E8DF]" data-testid="res-guest-name" />
                  </div>
                </div>

                {/* Date + Time */}
                <div className="grid grid-cols-2 gap-3 items-end">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-[#5C6B64]">Date</Label>
                    <Input type="date" value={form.date}
                      onChange={e => setForm(p => ({ ...p, date: e.target.value }))}
                      className="h-10 border-[#E2E8DF]" data-testid="res-date" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-[#5C6B64]">Time</Label>
                    <Input type="time" value={form.time}
                      onChange={e => setForm(p => ({ ...p, time: e.target.value }))}
                      className="h-10 border-[#E2E8DF]" data-testid="res-time" />
                  </div>
                </div>

                {/* Pax + Type */}
                <div className="grid grid-cols-2 gap-3 items-end">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-[#5C6B64]">Pax</Label>
                    <div className="relative">
                      <Users className="absolute left-2.5 top-2.5 w-4 h-4 text-[#5C6B64]" />
                      <Input type="number" min="1" value={form.pax}
                        onChange={e => setForm(p => ({ ...p, pax: e.target.value }))}
                        placeholder="Guests"
                        className="h-10 pl-9 border-[#E2E8DF]" data-testid="res-pax" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-[#5C6B64]">Type</Label>
                    <Select value={form.reservation_type} onValueChange={v => setForm(p => ({ ...p, reservation_type: v }))}>
                      <SelectTrigger className="h-10 border-[#E2E8DF]" data-testid="res-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="table">Table</SelectItem>
                        <SelectItem value="event">Event</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {isEvent && (
                  <>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-[#5C6B64]">Event Type</Label>
                      <Select value={form.event_type} onValueChange={v => setForm(p => ({ ...p, event_type: v }))}>
                        <SelectTrigger className="h-10 border-[#E2E8DF]" data-testid="res-event-type">
                          <SelectValue placeholder="Select event type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="birthday">Birthday</SelectItem>
                          <SelectItem value="corporate">Corporate</SelectItem>
                          <SelectItem value="celebration">Celebration</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-[#5C6B64]">Event Image</Label>
                      <label className="flex items-center gap-2 p-2.5 border border-dashed border-[#E2E8DF] rounded-lg cursor-pointer hover:bg-[#F3F5F2] transition-colors">
                        <Upload className="w-4 h-4 text-[#5C6B64]" />
                        <span className="text-xs text-[#5C6B64]">{imageFile ? imageFile.name : 'Upload reference image'}</span>
                        <input type="file" accept="image/*" className="hidden"
                          onChange={e => setImageFile(e.target.files?.[0] || null)}
                          data-testid="res-image-upload" />
                      </label>
                    </div>
                  </>
                )}

                <div className="space-y-1.5">
                  <Label className="text-xs text-[#5C6B64]">Notes</Label>
                  <Textarea value={form.notes}
                    onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                    placeholder="Special requests, dietary needs..."
                    rows={2}
                    className="border-[#E2E8DF] resize-none text-sm" data-testid="res-notes" />
                </div>

                <div className="flex items-center justify-between py-1">
                  <Label className="text-xs text-[#5C6B64]">Member</Label>
                  <Switch checked={form.is_member}
                    onCheckedChange={v => setForm(p => ({ ...p, is_member: v, member_id: v ? p.member_id : '' }))}
                    data-testid="res-member-toggle" />
                </div>

                {form.is_member && (
                  <div className="space-y-1.5">
                    <Label className="text-xs text-[#5C6B64]">Member ID / Number <span className="text-[#C45A41]">*</span></Label>
                    <div className="relative">
                      <Hash className="absolute left-2.5 top-2.5 w-4 h-4 text-[#5C6B64]" />
                      <Input value={form.member_id}
                        onChange={e => setForm(p => ({ ...p, member_id: e.target.value }))}
                        placeholder="Enter member ID"
                        className="h-10 pl-9 border-[#E2E8DF]" data-testid="res-member-id" />
                    </div>
                  </div>
                )}

                <Button type="submit" disabled={submitting}
                  className="w-full h-11 bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white font-medium active:scale-[0.98] transition-all"
                  data-testid="res-submit-btn">
                  {submitting ? 'Saving...' : 'Save Reservation'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── BOOKINGS LIST ─── */}
        <TabsContent value="list">
          <div className="flex items-center gap-2 flex-wrap mb-4">
            <Button variant="outline" size="sm"
              onClick={() => { setListMode('date'); setListDate(format(subDays(new Date(), 1), 'yyyy-MM-dd')); }}
              className={`h-8 text-xs border-[#E2E8DF] ${listMode === 'date' && listDate === format(subDays(new Date(), 1), 'yyyy-MM-dd') ? 'bg-[color:var(--color-primary)] text-white hover:bg-[color:var(--color-primary-hover)]' : 'text-[#5C6B64]'}`}
              data-testid="res-list-yesterday">Yesterday</Button>
            <Button variant="outline" size="sm"
              onClick={() => { setListMode('date'); setListDate(format(new Date(), 'yyyy-MM-dd')); }}
              className={`h-8 text-xs border-[#E2E8DF] ${listMode === 'date' && listDate === format(new Date(), 'yyyy-MM-dd') ? 'bg-[color:var(--color-primary)] text-white hover:bg-[color:var(--color-primary-hover)]' : 'text-[#5C6B64]'}`}
              data-testid="res-list-today">Today</Button>
            <Button variant="outline" size="sm"
              onClick={() => setListMode(m => m === 'upcoming' ? 'date' : 'upcoming')}
              className={`h-8 text-xs border-[#E2E8DF] ${listMode === 'upcoming' ? 'bg-[color:var(--color-primary)] text-white hover:bg-[color:var(--color-primary-hover)]' : 'text-[#5C6B64]'}`}
              data-testid="res-list-upcoming">Upcoming</Button>
            {listMode === 'date' && (
              <Input type="date" value={listDate}
                onChange={e => setListDate(e.target.value)}
                className="h-8 w-40 text-xs border-[#E2E8DF]" data-testid="res-list-date" />
            )}
            <Badge variant="outline" className="border-[#E2E8DF] text-[#5C6B64] text-xs ml-auto">
              {listMode === 'upcoming' ? upcomingBookings.length : reservations.length} booking{(listMode === 'upcoming' ? upcomingBookings.length : reservations.length) !== 1 ? 's' : ''}
            </Badge>
          </div>

          {(() => {
            const displayList = listMode === 'upcoming' ? upcomingBookings : reservations;
            return loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin w-6 h-6 border-2 border-[color:var(--color-primary)] border-t-transparent rounded-full" />
            </div>
          ) : displayList.length === 0 ? (
            <Card className="border-[#E2E8DF] shadow-sm">
              <CardContent className="py-10 text-center">
                <p className="text-sm text-[#5C6B64]">{listMode === 'upcoming' ? 'No upcoming reservations' : `No reservations for ${format(new Date(listDate + 'T00:00:00'), 'dd MMM yyyy')}`}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {displayList.map(r => {
                const st = STATUS_STYLES[r.status] || STATUS_STYLES.confirmed;
                const canAct = r.status === 'confirmed';
                const isCancelled = r.status === 'cancelled';
                const isNoShow = r.status === 'no-show';
                return (
                  <Card key={r.id} className="border-[#E2E8DF] shadow-sm" data-testid={`res-item-${r.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                            <span className="text-sm font-semibold text-[#1A2421]">{r.guest_name}</span>
                            <Badge className={`text-[10px] ${st.cls}`}>{st.label}</Badge>
                            {r.reservation_type === 'event' && (
                              <Badge variant="outline" className="text-[10px] border-[#C45A41]/30 text-[#C45A41]">
                                {r.event_type || 'Event'}
                              </Badge>
                            )}
                            {r.is_member && (
                              <Badge className="text-[10px] bg-[#D4A017]/10 text-[#D4A017] border-[#D4A017]/20">
                                <Crown className="w-2.5 h-2.5 mr-0.5" />Member{r.member_id ? ` #${r.member_id}` : ''}
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-[#5C6B64] flex-wrap">
                            {listMode === 'upcoming' && <span className="flex items-center gap-1 font-medium text-[color:var(--color-primary)]"><CalendarDays className="w-3 h-3" />{r.date}</span>}
                            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{r.time}</span>
                            <span className="flex items-center gap-1"><Users className="w-3 h-3" />{r.pax} pax</span>
                            {r.mobile && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{r.mobile}</span>}
                          </div>
                          {r.notes && <p className="text-xs text-[#5C6B64] mt-1.5 italic">{r.notes}</p>}
                        </div>
                        {/* Member toggle */}
                        <button
                          onClick={() => toggleMember(r.id, r.is_member)}
                          className={`shrink-0 p-1.5 rounded-lg border transition-colors ${r.is_member ? 'bg-[#D4A017]/10 border-[#D4A017]/30 text-[#D4A017]' : 'border-[#E2E8DF] text-[#5C6B64] hover:border-[#D4A017]/30'}`}
                          title={r.is_member ? 'Remove member' : 'Mark as member'}
                          data-testid={`res-member-btn-${r.id}`}>
                          <Crown className="w-4 h-4" />
                        </button>
                      </div>
                      {/* Action buttons */}
                      <div className="flex items-center gap-2 flex-wrap">
                        {canAct && (
                          <>
                            <Button size="sm"
                              className="h-9 px-4 bg-[#2D6A4F] hover:bg-[#2D6A4F]/90 text-white text-xs font-medium gap-1.5"
                              onClick={() => updateStatus(r.id, 'arrived')}
                              data-testid={`res-arrive-${r.id}`}>
                              <CheckCircle2 className="w-3.5 h-3.5" /> Arrived
                            </Button>
                            <Button size="sm" variant="outline"
                              className="h-9 px-4 border-[#C45A41]/30 text-[#C45A41] hover:bg-[#C45A41]/5 text-xs font-medium gap-1.5"
                              onClick={() => updateStatus(r.id, 'cancelled')}
                              data-testid={`res-cancel-${r.id}`}>
                              <XCircle className="w-3.5 h-3.5" /> Cancel
                            </Button>
                            <Button size="sm" variant="outline"
                              className="h-9 px-4 border-[#D4A017]/30 text-[#D4A017] hover:bg-[#D4A017]/5 text-xs font-medium gap-1.5"
                              onClick={() => updateStatus(r.id, 'no-show')}
                              data-testid={`res-noshow-${r.id}`}>
                              <AlertTriangle className="w-3.5 h-3.5" /> No-show
                            </Button>
                            <Button size="sm" variant="outline"
                              className="h-9 px-4 border-[#6366F1]/30 text-[#6366F1] hover:bg-[#6366F1]/5 text-xs font-medium gap-1.5"
                              onClick={() => updateStatus(r.id, 'rescheduled')}
                              data-testid={`res-reschedule-${r.id}`}>
                              <RefreshCw className="w-3.5 h-3.5" /> Reschedule
                            </Button>
                          </>
                        )}
                        {(isCancelled || isNoShow) && (
                          <Button size="sm" variant="outline"
                            className="h-9 px-4 border-[#2D6A4F]/30 text-[#2D6A4F] hover:bg-[#2D6A4F]/5 text-xs font-medium gap-1.5"
                            onClick={() => updateStatus(r.id, 'confirmed')}
                            data-testid={`res-restore-${r.id}`}>
                            <RotateCcw className="w-3.5 h-3.5" /> Restore
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          );
          })()}
        </TabsContent>

        {/* ─── CALENDAR VIEW ─── */}
        <TabsContent value="calendar">
          {(() => {
            const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
            const daysInMonth = new Date(calYear, calMonth, 0).getDate();
            const firstDayOfWeek = new Date(calYear, calMonth - 1, 1).getDay();
            const dates = calendarData?.dates || {};

            return (
              <Card className="border-[#E2E8DF] shadow-sm" data-testid="res-calendar-card">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <Button variant="ghost" size="sm" onClick={() => {
                      if (calMonth === 1) { setCalMonth(12); setCalYear(y => y - 1); }
                      else setCalMonth(m => m - 1);
                    }} data-testid="cal-prev-month"><ChevronLeft className="w-4 h-4" /></Button>
                    <CardTitle className="text-base font-semibold text-[#1A2421]">
                      {MONTH_NAMES[calMonth - 1]} {calYear}
                    </CardTitle>
                    <Button variant="ghost" size="sm" onClick={() => {
                      if (calMonth === 12) { setCalMonth(1); setCalYear(y => y + 1); }
                      else setCalMonth(m => m + 1);
                    }} data-testid="cal-next-month"><ChevronRight className="w-4 h-4" /></Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-7 gap-px bg-[#E2E8DF] rounded-lg overflow-hidden">
                    {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
                      <div key={d} className="bg-[#F9F9F8] text-center py-2 text-[10px] font-semibold text-[#5C6B64] uppercase">{d}</div>
                    ))}
                    {Array.from({ length: firstDayOfWeek }).map((_, i) => (
                      <div key={`e-${i}`} className="bg-white p-2 min-h-[70px]" />
                    ))}
                    {Array.from({ length: daysInMonth }).map((_, i) => {
                      const day = i + 1;
                      const dateKey = `${calYear}-${String(calMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                      const d = dates[dateKey];
                      const totalBookings = d ? d.tables + d.events : 0;
                      const totalPax = d ? d.table_pax + d.event_pax : 0;
                      const isToday = dateKey === format(new Date(), 'yyyy-MM-dd');

                      return (
                        <div key={day}
                          className={`bg-white p-1.5 min-h-[70px] cursor-pointer hover:bg-[#F3F5F2] transition-colors ${isToday ? 'ring-1 ring-[var(--color-primary)]' : ''}`}
                          onClick={() => d && setCalPopup({ date: dateKey, guests: d.guests || [] })}
                          data-testid={`cal-day-${day}`}>
                          <span className={`text-xs font-medium ${isToday ? 'text-[color:var(--color-primary)]' : 'text-[#1A2421]'}`}>{day}</span>
                          {totalBookings > 0 && (
                            <div className="mt-1 space-y-0.5">
                              <div className="text-[10px] font-medium text-[color:var(--color-primary)] bg-[color:var(--color-primary)]/8 rounded px-1 py-0.5 text-center">
                                {totalBookings} {totalBookings === 1 ? 'booking' : 'bookings'}
                              </div>
                              <div className="text-[10px] text-[#5C6B64] text-center">{totalPax} pax</div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            );
          })()}

          {/* Calendar Day Popup */}
          <AlertDialog open={!!calPopup} onOpenChange={open => !open && setCalPopup(null)}>
            <AlertDialogContent className="max-w-md">
              <AlertDialogHeader>
                <AlertDialogTitle className="text-[#1A2421] flex items-center justify-between">
                  <span>{calPopup?.date ? format(new Date(calPopup.date + 'T00:00:00'), 'dd MMMM yyyy') : ''}</span>
                  <Button variant="ghost" size="sm" onClick={() => setCalPopup(null)} className="h-7 w-7 p-0" data-testid="cal-popup-close">
                    <X className="w-4 h-4" />
                  </Button>
                </AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {calPopup?.guests?.length === 0 ? (
                      <p className="text-sm text-[#5C6B64] py-4 text-center">No bookings</p>
                    ) : calPopup?.guests?.map((g, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-[#F9F9F8] rounded-lg border border-[#E2E8DF]" data-testid={`cal-guest-${i}`}>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-[#1A2421]">{g.name}</p>
                          <div className="flex items-center gap-2 text-xs text-[#5C6B64]">
                            {g.time && <span>{g.time}</span>}
                            <span className="capitalize">{g.type}</span>
                            {g.status && (
                              <Badge className={`text-[10px] ${(STATUS_STYLES[g.status] || STATUS_STYLES.confirmed).cls}`}>
                                {(STATUS_STYLES[g.status] || STATUS_STYLES.confirmed).label}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <span className="text-sm font-semibold text-[#1A2421]">{g.pax}</span>
                          <span className="text-xs text-[#5C6B64] ml-1">pax</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="text-[#5C6B64]" data-testid="cal-popup-done">Close</AlertDialogCancel>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </TabsContent>
      </Tabs>

      {/* Duplicate booking warning */}
      <AlertDialog open={!!dupWarning} onOpenChange={(open) => { if (!open) { setDupWarning(null); setPendingSubmit(false); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#1A2421]">Duplicate Booking</AlertDialogTitle>
            <AlertDialogDescription className="text-[#5C6B64]">
              A booking already exists for this number on {form.date}.
              {dupWarning && (
                <span className="block mt-1 font-medium text-[#1A2421]">
                  {dupWarning.guest_name} at {dupWarning.time}
                </span>
              )}
              <span className="block mt-2">Proceed anyway?</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="text-[#5C6B64]" data-testid="dup-cancel">No, Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white"
              data-testid="dup-confirm"
              onClick={() => { setDupWarning(null); doSubmit(); }}>
              Yes, Proceed
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
